import { Flex, Tooltip } from 'antd';
import React, { CSSProperties, forwardRef } from 'react';
import { Button } from './ButtonGroup.styles';

export interface ButtonGroupOption {
  label: string;
  value: string;
  disabled?: boolean;
  tooltip?: string; // Add tooltip support
  dataCy?: string; // Add data-cy support
}

interface ButtonGroupProps {
  options: ButtonGroupOption[];
  value?: string;
  onChange?: (value: string) => void;
  disabled?: boolean;
  className?: string;
  style?: CSSProperties;
  size?: 'small' | 'middle' | 'large';
}

const ButtonGroup = forwardRef<HTMLDivElement, ButtonGroupProps>(
  (
    {
      options,
      value,
      onChange,
      disabled = false,
      className,
      style,
      size = 'middle',
    },
    ref,
  ) => {
    const getButtonPosition = (index: number) => {
      if (index === 0) return 'first';
      if (index === options.length - 1) return 'last';
      return 'middle';
    };

    const handleChange = (newValue: string) => {
      if (onChange) {
        onChange(newValue);
      }
    };

    const renderButton = (option: ButtonGroupOption, index: number) => {
      const isDisabled = disabled || option.disabled || false;
      const button = (
        <Button
          key={option.value}
          $position={getButtonPosition(index)}
          $active={value === option.value}
          $disabled={isDisabled}
          disabled={isDisabled}
          onClick={() => handleChange(option.value)}
          size={size}
          data-cy={option.dataCy}
        >
          {option.label}
        </Button>
      );

      if (option.tooltip) {
        return (
          <Tooltip key={option.value} title={option.tooltip}>
            <span style={{ display: 'flex', flex: 1 }}>{button}</span>
          </Tooltip>
        );
      }

      return button;
    };

    return (
      <Flex ref={ref} className={className} style={style}>
        {options.map((option, index) => renderButton(option, index))}
      </Flex>
    );
  },
);

ButtonGroup.displayName = 'ButtonGroup';

export default ButtonGroup;
